// Fix CM (2026-09-13): pantalla global de Tareas y Flujo de Trabajo. Junta las tareas de
// todos los módulos, deja filtrar por módulo, muestra mis pendientes y las que están
// frenadas, y guarda las preferencias de notificación de cada persona (alertas en el ERP
// y, si la empresa contrata la API de WhatsApp, también por WhatsApp).
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { ListTodo, Bell, AlertTriangle, X } from "lucide-react";
import { API } from "../contexts/AuthContext";
import TareasPanel from "../components/TareasPanel";

const opts = { withCredentials: true };
// Fix CR (2026-09-14): "englobar INGENIERIA y PRESUPUESTOS en uno sólo llamado
// GABINETE" (Heber, 14/09). Es una sola pestaña que trae las tareas de los dos
// módulos, automáticas y manuales. Nada se migra: el backend agrupa al leer.
const MODULOS = [["", "Todos los módulos"], ["compras", "Compras"], ["gabinete", "Gabinete"],
  ["obras", "Obras"], ["tesoreria", "Tesorería"],
  ["inventario", "Inventario"], ["rrhh", "RRHH"], ["finanzas", "Finanzas"], ["comercial", "Comercial"], ["general", "General"]];

function PreferenciasModal({ onClose }) {
  const [p, setP] = useState({ erp: true, whatsapp: false, telefono: "", whatsapp_configurado: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tareas/preferencias`, opts)
      .then(({ data }) => setP(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tareas/preferencias`, { erp: p.erp, whatsapp: p.whatsapp, telefono: p.telefono }, opts);
      toast.success("Preferencias guardadas");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9995] flex items-center justify-center p-4" data-testid="preferencias-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-bold text-gray-900">Preferencias de notificación</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer">
            <input type="checkbox" checked={!!p.erp} onChange={e => setP({ ...p, erp: e.target.checked })} data-testid="pref-erp" />
            <span className="text-sm text-gray-700">Recibir alertas en el ERP (campana)</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer">
            <input type="checkbox" checked={!!p.whatsapp} onChange={e => setP({ ...p, whatsapp: e.target.checked })} data-testid="pref-whatsapp" />
            <span className="text-sm text-gray-700">Recibir alertas por WhatsApp (solo urgentes y bloqueantes)</span>
          </label>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Teléfono en formato internacional</label>
            <input value={p.telefono || ""} onChange={e => setP({ ...p, telefono: e.target.value })} placeholder="+595..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="pref-telefono" />
          </div>
          {!p.whatsapp_configurado && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              El envío por WhatsApp todavía no está conectado: falta la API oficial de WhatsApp Business.
              Podés dejar tu número cargado desde ahora.
            </p>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="flex-1 bg-[#2E6DA4] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="pref-guardar-btn">
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Tareas() {
  const { tareaId } = useParams();
  const navigate = useNavigate();
  const [modulo, setModulo] = useState("");
  const [resumen, setResumen] = useState(null);
  const [prefs, setPrefs] = useState(false);
  const [destacada, setDestacada] = useState(null);

  const cargarResumen = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tareas/resumen`, opts);
      setResumen(data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  // Enlace directo desde la campana o desde WhatsApp: /tareas/<id>
  useEffect(() => {
    if (!tareaId) return;
    axios.get(`${API}/tareas/${tareaId}`, opts)
      .then(({ data }) => { setDestacada(data); if (data?.modulo) setModulo(data.modulo); })
      .catch(() => toast.error("No se encontró la tarea"));
  }, [tareaId]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="tareas-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="text-xs font-bold text-[#2E6DA4] bg-[#EBF3FB] px-2 py-0.5 rounded-full uppercase tracking-wide">Flujo de trabajo</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mt-1 flex items-center gap-2">
            <ListTodo size={26} className="text-[#2E6DA4]" /> Tareas
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Lo que genera el sistema y lo que cargan las personas, módulo por módulo</p>
        </div>
        <button onClick={() => setPrefs(true)} className="flex items-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="tareas-prefs-btn">
          <Bell size={15} /> Mis notificaciones
        </button>
      </div>

      {resumen && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tareas-resumen">
          <div className="bg-white rounded-xl border border-gray-200/60 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase">Mis pendientes</p>
            <p className="text-xl font-bold text-[#2E6DA4]">{resumen.mis_pendientes}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200/60 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase">Mis vencidas</p>
            <p className="text-xl font-bold text-red-600">{resumen.mis_vencidas}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200/60 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase">Frenadas +48 h</p>
            <p className="text-xl font-bold text-amber-600">{resumen.frenadas}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200/60 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase">Abiertas en total</p>
            <p className="text-xl font-bold text-gray-800">{resumen.total}</p>
          </div>
        </div>
      )}

      {destacada && (
        <div className="bg-[#EBF3FB] border border-[#2E6DA4]/30 rounded-2xl p-4 flex items-start gap-3" data-testid="tarea-destacada">
          <AlertTriangle size={16} className="text-[#2E6DA4] mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900">{destacada.titulo}</p>
            <p className="text-xs text-gray-600">{destacada.entidad_label} · {destacada.asignado_rol || destacada.asignado_nombre || "sin asignar"}</p>
          </div>
          {destacada.accion_url && (
            <button onClick={() => navigate(destacada.accion_url)} className="px-3 py-1.5 rounded-lg bg-[#2E6DA4] text-white text-xs font-semibold" data-testid="tarea-destacada-ir">
              Ir a resolverla
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5" data-testid="tareas-modulos">
        {MODULOS.map(([v, l]) => (
          <button key={v || "todos"} onClick={() => setModulo(v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${modulo === v ? "bg-[#2E6DA4] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            data-testid={`tareas-modulo-${v || "todos"}`}>
            {l}{resumen?.por_modulo?.[v] ? ` (${resumen.por_modulo[v]})` : ""}
          </button>
        ))}
      </div>

      <TareasPanel modulo={modulo} titulo={modulo ? `Tareas de ${(MODULOS.find(m => m[0] === modulo) || [])[1]}` : "Todas las tareas"} />

      {prefs && <PreferenciasModal onClose={() => setPrefs(false)} />}
    </div>
  );
}
